import type { ActionFunctionArgs } from "react-router";
import { Form } from "react-router";

import {
  Box,
  DarkMode,
  Flex,
  Heading,
  Image,
  theme,
} from "@vygruppen/spor-react";

import { authenticateWithCognito } from "~/features/auth/auth.server";
import { returnToCookie } from "~/features/auth/return-to-cookie.server";
import { commonLogger } from "~/features/logging/logging.server";
import { NetworkDependentButton } from "~/features/ui/NetworkDependentButton";

export async function action({ request }: ActionFunctionArgs) {
  const returnTo = new URL(request.url).searchParams.get("returnTo");

  try {
    // This will throw a redirect to Cognito if no session exists
    // If session exists, it returns the session cookie
    const sessionCookie = await authenticateWithCognito(request);

    // If we get here, we have a valid session - shouldn't normally happen
    // on initial login since the OAuth flow requires a redirect
    return new Response(null, {
      headers: {
        "Set-Cookie": sessionCookie,
      },
    });
  } catch (error) {
    // OAuth redirect - add returnTo cookie and re-throw
    // Using reloadDocument on the form means this is a traditional form submission,
    // so the browser will handle the redirect normally
    if (isRedirect(error) && returnTo) {
      error.headers.append(
        "Set-Cookie",
        await returnToCookie.serialize(returnTo),
      );
    }

    if (!isRedirect(error)) {
      commonLogger.error("authentication_error", { error });
    }

    throw error;
  }
}

function isRedirect(error: unknown): error is Response {
  return (
    error instanceof Response &&
    error.status >= 300 &&
    error.status < 400 &&
    error.headers.has("Location")
  );
}

export default function Login() {
  // TODO: Fallback to local auth somehow
  return (
    <Box
      display="flex"
      flexDirection="column"
      backgroundColor="teal.500"
      p={0}
      alignItems="center"
      justifyContent="center"
      flex={1}
      maxW="full"
      mx={0}
      height="100dvh"
      as="main"
    >
      <Image src="/illustrations/stasjon-stor-dark.svg" alt="" height="25vh" />
      <Heading as="h2" fontWeight="bold" variant="xl-display" color="white">
        Velkommen!
      </Heading>
      <Flex
        as={Form}
        gap={6}
        justifyContent="center"
        width="full"
        method="post"
        reloadDocument
      >
        <DarkMode>
          <NetworkDependentButton
            type="submit"
            margin={theme.spacing[4]}
            variant="primary"
            size="lg"
            width="md"
          >
            Logg inn
          </NetworkDependentButton>
        </DarkMode>
      </Flex>
    </Box>
  );
}
